/**
 * The page's session: the refresh of its token, and the sign-out when the session ended. The
 * cloud funnel and the sign-in requests use it on a 401.
 */

import { toast } from 'sonner';

import { authSession } from '@/auth/authSession';
import type { RefreshResponse } from '@/types/auth';
import { RefreshOutcome, RefreshResponseSchema } from '@/types/auth';
import { ViolationSourceKind } from '@/types/contract';

import { getAnswerOrigin, JsonTextSchema, parseOne } from './contract';
import { fetchWithCookie, httpError } from './http';
import { HTTP_STATUS } from './httpStatus';
import { SessionExpiry } from './sessionExpiry';
import { listenVisible, scheduleCancelable, SessionRefresher } from './sessionRefresh';
import { getSessionExpiry } from './sessionToken';

const REFRESH_URL = '/vms/api/v1/auth/refresh';

function getPageLocation(): Location | null {
    return typeof window === 'undefined' ? null : window.location;
}

function showErrorToast(message: string): void {
    toast.error(message);
}

// A bare setTimeout, called as a method of another object, throws "Illegal invocation".
function scheduleTimer(callback: () => void, delayMs: number): void {
    setTimeout(callback, delayMs);
}

export const sessionExpiry = new SessionExpiry({
    authSession,
    getLocation: getPageLocation,
    showToast: showErrorToast,
    schedule: scheduleTimer,
});

/** The next session token, or null when the cloud says that the session ended. */
async function requestRefresh(): Promise<RefreshResponse | null> {
    const resp = await fetchWithCookie(REFRESH_URL, { method: 'POST' });
    if (resp.status === HTTP_STATUS.UNAUTHORIZED) {
        return null;
    }
    if (!resp.ok) {
        throw await httpError(resp);
    }
    const origin = getAnswerOrigin(REFRESH_URL, resp.status, { kind: ViolationSourceKind.Cloud });
    return parseOne(RefreshResponseSchema, parseOne(JsonTextSchema, await resp.text(), origin),
        origin);
}

export const sessionRefresher = new SessionRefresher({
    authSession,
    request: requestRefresh,
    getTokenExpiry: getSessionExpiry,
    onEnded: (): void => {
        sessionExpiry.handle();
    },
    onVisible: listenVisible,
    schedule: scheduleCancelable,
    now: Date.now,
});

/**
 * A request refused for its expired token goes once more after a refresh. A 401 is refused before
 * the handler runs, so a write is not done twice.
 */
export async function fetchWithCookieRefresh(
    url: string, init: RequestInit = {},
): Promise<Response> {
    const resp = await fetchWithCookie(url, init);
    if (resp.status !== HTTP_STATUS.UNAUTHORIZED ||
        await sessionRefresher.refresh() !== RefreshOutcome.Refreshed) {
        return resp;
    }
    return await fetchWithCookie(url, init);
}
